import { lazy, useCallback, useMemo, useRef, useState } from "react"

import Heading from "$/components/Heading/Heading"
import { CRONOFY_WIDGET } from "$/constants"
import { axiosClient } from "$/http"
import { getErrorMessages } from "$/utils/get-error-messages"

import {
    Button,
    ETypographyAlignment,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    Typography
} from "@/3514/components"
import Urls from "@/api/urls"
// WISER-5808
import "@/assets/coach.scss"
import { useAvailability } from "@/entities/coach-calendar/api/use-availavility-rules-attrs"
import { useCoachCalendarOrderNavigation } from "@/entities/coach-calendar/hooks"
import { SimpleLayout } from "@/layouts"
import DateWidget from "@/shared/DateWidget"
// WISER-5808
import { WarningMessage } from "@/shared/WarningMessage"
import { FullSpinner } from "@/shared/spinner"

import { CoachCalendarStepUrls } from "./constants"
import { StepsNavigator } from "./ui/StepNavigator"

const LazyCronofyWidget = lazy(() => import("$/components/CronofyWidgets/CronofyWidget"))

type Props = {
    order?: number
}

const AvailabilityStep = ({ order }: Props) => {
    const { navigateByOrder } = useCoachCalendarOrderNavigation(order)
    const viewerRef = useRef<HTMLDivElement>(null)
    const { data, isLoading } = useAvailability()
    const [rules, setRules] = useState(null)
    const [error, setError] = useState(null)

    const onClick = async () => {
        try {
            const btn = viewerRef?.current?.querySelector(".av__submit") as HTMLButtonElement
            btn?.click()
            if (rules) {
                await axiosClient.post(Urls.avRules(), rules.availability_rule)
            }
            navigateByOrder()
        } catch (error) {
            setError(getErrorMessages(error))
        }
    }

    const onAvailabilitySelect = useCallback(async data => {
        if (data) {
            try {
                setRules(data)
            } catch (error) {
                setError(getErrorMessages(error))
            }
        }
    }, [])

    const memoAvailabilityRules = useMemo(
        () => (
            <LazyCronofyWidget
                ref={viewerRef}
                widget={CRONOFY_WIDGET.AvailabilityRules}
                id="availability-rules-calendar"
                options={{
                    ...(data as any),
                    styles: {
                        prefix: "av"
                    },
                    callback: onAvailabilitySelect
                }}
            />
        ),
        [data]
    )

    return (
        <SimpleLayout
            header={<DateWidget className="coach-date-title" />}
            headerVariant="gray"
            showBackBtn
            showProfilePhoto
            className="coach-edit-availability-layout"
        >
            <div className="coach-calendar-wrapper mx-auto pb-5">
                <div className="d-flex justify-content-center mt-5">
                    <StepsNavigator activeStep={CoachCalendarStepUrls.AVAILABILITY_STEP} />
                </div>
                <Heading textAlign="center" tag="h6" fontSize={16} className="mb-[40px] mt-5 text-accentOld">
                    Step {order}
                </Heading>
                <Typography
                    text="Edit Your Working Hours"
                    className="mb-2"
                    alignment={ETypographyAlignment.Center}
                    tag={ETypographyTag.Heading1}
                    weight={ETypographyWeight.Medium}
                    size={ETypographySize.Heading1}
                    family={ETypographyFamily.DmSerif}
                />

                <p className="color-gray fs-14 text-center mb-0">You may now manually tweak your available hours.</p>
                <p className="color-gray fs-14 text-center mb-[40px]">
                    Drag your mouse up & down across the grid to adjust your availability.
                </p>

                {isLoading ? <FullSpinner /> : memoAvailabilityRules}
                <WarningMessage message={error} />
                <div className="mx-auto w-min mt-[50px]">
                    <Button className="!bg-accentOld" onClick={onClick}>
                        Next
                    </Button>
                </div>
            </div>
        </SimpleLayout>
    )
}

export default AvailabilityStep
